import React from 'react';
import { Box, Typography, Button, Fade } from '@mui/material';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';

interface ConfigErrorScreenProps {
  code?: number | null;
  onRetry: () => void;
}

const getErrorContent = (code?: number | null) => {
  if (code === 400) {
    return {
      title: 'Request Could Not Be Processed',
      subtitle:
        'We could not validate your configuration request. Please retry, and contact support if this continues.',
      badgeText: '400 Bad Request',
      accent: '#d97706',
      glow: 'rgba(217, 119, 6, 0.25)',
    };
  }

  return {
    title: 'Configuration Service Unavailable',
    subtitle:
      'We are unable to load your configuration right now. Please try again in a moment.',
    badgeText: '500 Server Error',
    accent: '#b91c1c',
    glow: 'rgba(185, 28, 28, 0.22)',
  };
};

const ConfigErrorScreen: React.FC<ConfigErrorScreenProps> = ({ code, onRetry }) => {
  const content = getErrorContent(code);

  return (
    <Fade in timeout={250}>
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background:
            'radial-gradient(circle at 15% 20%, rgba(0, 63, 45, 0.08) 0, rgba(0, 63, 45, 0) 35%), radial-gradient(circle at 85% 75%, rgba(15, 118, 110, 0.07) 0, rgba(15, 118, 110, 0) 35%), linear-gradient(135deg, #f6faf9 0%, #ffffff 60%)',
          p: 3,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 640,
            borderRadius: 3,
            p: { xs: 3, sm: 4 },
            background: '#ffffff',
            border: '1px solid #dbe7e3',
            boxShadow: `0 20px 45px ${content.glow}`,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 6,
              background: `linear-gradient(90deg, ${content.accent} 0%, #003f2d 100%)`,
            }}
          />

          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 1.5,
              py: 0.5,
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 700,
              color: content.accent,
              background: `${content.glow}`,
              mb: 2,
            }}
          >
            {content.badgeText}
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1f2937', mb: 1.2 }}>
            {content.title}
          </Typography>

          <Typography variant="body1" sx={{ color: '#4b5563', lineHeight: 1.7, mb: 3.2 }}>
            {content.subtitle}
          </Typography>

          <Button
            variant="contained"
            startIcon={<ReplayRoundedIcon />}
            onClick={onRetry}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#003f2d',
              '&:hover': {
                backgroundColor: '#0b5d45',
              },
            }}
          >
            Retry
          </Button>
        </Box>
      </Box>
    </Fade>
  );
};

export default ConfigErrorScreen;